import { useNavigate, useParams } from 'react-router-dom';
import { Box, Flex, Icon, SimpleGrid, Text } from '@chakra-ui/react';
import {
  ViewfinderCircleIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  Square3Stack3DIcon,
  DocumentIcon,
  BookOpenIcon,
  EllipsisHorizontalIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import useContentTypesStore from '@/utils/use-content-type';
import useCanManageContentTypes from '@/lib/hooks/use-can-manage-content-types';
import { slotFor } from '@/lib/slot-color';

export type PickerMode = 'create' | 'browse';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  mode: PickerMode;
};

type IconChoice = {
  icon: typeof ViewfinderCircleIcon;
  description: string;
};

// Pick a semantic icon + blurb per content type. Colour is NOT chosen here —
// it comes from the DS tag-slot palette (slotFor), so it groups by type name
// rather than by meaning, per the design system.
function pickIcon(name: string): IconChoice {
  const n = name.toLowerCase();
  if (n.includes('drill')) {
    return { icon: ViewfinderCircleIcon, description: 'Practice exercise' };
  }
  if (n.includes('skill')) {
    return { icon: SparklesIcon, description: 'Technique breakdown' };
  }
  if (n.includes('play')) {
    return { icon: ArrowTrendingUpIcon, description: 'Diagram with routes' };
  }
  if (n.includes('tactic')) {
    return { icon: Square3Stack3DIcon, description: 'Game situation' };
  }
  if (n.includes('document')) {
    return { icon: DocumentIcon, description: 'PDF or handout' };
  }
  if (n.includes('article') || n.includes('post')) {
    return { icon: BookOpenIcon, description: 'Long-form read' };
  }
  return { icon: DocumentIcon, description: 'Workspace content' };
}

function pluralize(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return trimmed;
  if (/s$/i.test(trimmed)) return trimmed;
  if (/y$/i.test(trimmed)) return trimmed.replace(/y$/i, 'ies');
  return `${trimmed}s`;
}

const COPY: Record<PickerMode, { title: string; subtitle: string }> = {
  create: {
    title: 'What do you want to create?',
    subtitle: 'Start a blank item in a library — a drill, play, or article.',
  },
  browse: {
    title: 'What do you want to browse?',
    subtitle: 'Jump into one of your content libraries.',
  },
};

export default function ContentTypePickerDialog({ isOpen, onClose, mode }: Props) {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { contentTypes } = useContentTypesStore();
  const copy = COPY[mode];
  const canManageContentTypes = useCanManageContentTypes();

  const targetForType = (contentTypeId: number) =>
    mode === 'create'
      ? `/${workspaceId}/content-types/${contentTypeId}/contents/create`
      : `/${workspaceId}/content-types/${contentTypeId}/contents/workspace-library`;

  const handleSelect = (contentTypeId: number) => {
    onClose();
    navigate(targetForType(contentTypeId));
  };

  const handleOther = () => {
    onClose();
    navigate(`/${workspaceId}/content-types`);
  };

  const tileLabel = (name: string) =>
    mode === 'create' ? `New ${name.toLowerCase()}` : pluralize(name);

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
      <DialogBackdrop />
      <DialogContent maxW='600px'>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <Text fontSize='sm' color='fg.muted' mt={1}>
            {copy.subtitle}
          </Text>
        </DialogHeader>
        <DialogBody pb={6}>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} mb={3}>
            {contentTypes.map((ct) => {
              const choice = pickIcon(ct.name);
              const slot = slotFor(ct.name);
              return (
                <Flex
                  key={ct.id}
                  as='button'
                  type='button'
                  onClick={() => handleSelect(ct.id)}
                  align='center'
                  gap={3}
                  p={3}
                  borderWidth={1}
                  borderColor='border'
                  borderRadius='lg'
                  bg='bg.panel'
                  textAlign='left'
                  width='100%'
                  transition='all 0.15s'
                  _hover={{ borderColor: 'border.emphasized', bg: 'bg.subtle' }}
                >
                  <Flex
                    align='center'
                    justify='center'
                    w={10}
                    h={10}
                    borderRadius='md'
                    bg={`slot${slot}.bg`}
                    flexShrink={0}
                  >
                    <Icon as={choice.icon} w={5} h={5} color={`slot${slot}.fg`} />
                  </Flex>
                  <Box flex='1' minW={0}>
                    <Text fontSize='sm' fontWeight='semibold' color='fg.strong'>
                      {tileLabel(ct.name)}
                    </Text>
                    <Text fontSize='xs' color='fg.muted'>
                      {choice.description}
                    </Text>
                  </Box>
                  <Icon as={ChevronRightIcon} w={4} h={4} color='fg.subtle' flexShrink={0} />
                </Flex>
              );
            })}
          </SimpleGrid>
          {canManageContentTypes && (
            <Flex
              as='button'
              type='button'
              onClick={handleOther}
              align='center'
              justify='center'
              gap={2}
              p={3}
              borderWidth={1}
              borderColor='border.emphasized'
              borderRadius='lg'
              borderStyle='dashed'
              bg='transparent'
              width='100%'
              transition='all 0.15s'
              _hover={{ bg: 'bg.subtle' }}
            >
              <Icon as={EllipsisHorizontalIcon} w={5} h={5} color='fg.muted' />
              <Text fontSize='sm' fontWeight='medium' color='fg'>
                Other
              </Text>
            </Flex>
          )}
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
